import { OptionPair } from "@erp/shared-libs/common";

/**
 * 選項超過這個數量才顯示搜尋框（少量選項一眼看得完，搜尋框只會佔位置）
 */
export const SELECT_MENU_SEARCH_THRESHOLD = 8;

/**
 * 依關鍵字篩選選項：比對顯示名稱，不分大小寫、包含就算符合；關鍵字是空白時回傳全部
 */
export const filterSelectMenuOptions = (options: OptionPair[], keyword: string): OptionPair[] => {
  const normalizedKeyword = keyword.trim().toLowerCase();

  if (normalizedKeyword === "") {
    return options;
  }

  return options.filter((option) => option.label.toLowerCase().includes(normalizedKeyword));
};

/**
 * 把已包含與已排除的選項排到最前面：包含在前、排除在後、其餘在最後，三組各自維持原本的順序。
 * 同時在兩邊的值算包含
 */
export const orderSelectedFirst = (
  options: OptionPair[],
  selectedValues: ReadonlySet<string | number>,
  excludedValues: ReadonlySet<string | number> = new Set()
): OptionPair[] => [
  ...options.filter((option) => selectedValues.has(option.value)),
  ...options.filter(
    (option) => !selectedValues.has(option.value) && excludedValues.has(option.value)
  ),
  ...options.filter(
    (option) => !selectedValues.has(option.value) && !excludedValues.has(option.value)
  ),
];

/**
 * 多選：切換某個值的勾選狀態，回傳新的陣列（原本有就拿掉，沒有就加在最後）
 */
export const toggleSelectMenuValue = <T extends string | number>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter((x) => x !== value) : [...values, value];

/**
 * 把顯示名稱依關鍵字切成三段（符合處前、符合處、符合處後），給清單把符合的字標成主色。
 * 沒有關鍵字或不符合時 `match` 是空字串
 */
export const splitLabelByKeyword = (
  label: string,
  keyword: string
): { before: string; match: string; after: string } => {
  const normalizedKeyword = keyword.trim().toLowerCase();
  const matchIndex = normalizedKeyword === "" ? -1 : label.toLowerCase().indexOf(normalizedKeyword);

  if (matchIndex === -1) {
    return { before: label, match: "", after: "" };
  }

  return {
    before: label.slice(0, matchIndex),
    match: label.slice(matchIndex, matchIndex + normalizedKeyword.length),
    after: label.slice(matchIndex + normalizedKeyword.length),
  };
};

/**
 * 選項能不能選：`disabled` 或帶了不能選的原因都不能選
 */
export const isSelectMenuOptionDisabled = (option: OptionPair) =>
  option.disabled === true || option.disabledReason != null;

/**
 * 鍵盤上下鍵要移到哪一列：從目前那一列往指定方向找下一個可以選的選項，到頭就繞回另一端。
 *
 * @param options 清單上的選項
 * @param currentIndex 目前停在哪一列；沒有停在任何一列傳 -1
 * @param step 1＝往下，-1＝往上
 * @returns 下一列的位置；沒有任何可以選的選項時回 -1
 */
export const findNextEnabledIndex = (
  options: OptionPair[],
  currentIndex: number,
  step: 1 | -1
): number => {
  const count = options.length;

  // 從 -1 往上要落在最後一列，所以起點補成「清單之外的下一格」
  let index = currentIndex === -1 && step === -1 ? count : currentIndex;

  for (let moved = 0; moved < count; moved++) {
    index = (index + step + count) % count;

    if (!isSelectMenuOptionDisabled(options[index])) {
      return index;
    }
  }

  return -1;
};

/**
 * 選單的選取狀態：一個值只會是沒選、包含、排除其中一種
 */
export interface SelectMenuSelectionState<T extends string | number> {
  /**
   * 包含的值；單選最多一個
   */
  values: T[];
  /**
   * 排除的值
   */
  excludedValues: T[];
}

/**
 * 從陣列拿掉某個值；原本就沒有時回傳同一個陣列（呼叫端用參考是否相同判斷有沒有變）
 */
const removeSelectMenuValue = <T extends string | number>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter((x) => x !== value) : values;

/**
 * 點列本身（包含的切換），回傳新的狀態；沒有變的那一邊回傳原本的陣列。
 *
 * - 多選：沒選 → 包含；包含 → 沒選；排除 → 包含（同時從排除拿掉）
 * - 單選：選定這個值並把排除清空；可清空時再點一次選中的會取消
 *
 * @param state 目前的狀態
 * @param value 被點的值
 * @param isMultiple 是不是多選
 * @param isClearable 單選可不可以清空（多選不看這個）
 */
export const toggleSelectMenuInclusion = <T extends string | number>(
  state: SelectMenuSelectionState<T>,
  value: T,
  isMultiple: boolean,
  isClearable = true
): SelectMenuSelectionState<T> => {
  const isIncluded = state.values.includes(value);

  if (isMultiple) {
    return isIncluded
      ? { values: removeSelectMenuValue(state.values, value), excludedValues: state.excludedValues }
      : {
          values: [...state.values, value],
          excludedValues: removeSelectMenuValue(state.excludedValues, value),
        };
  }

  return {
    values: !isIncluded ? [value] : isClearable ? [] : state.values,
    excludedValues: state.excludedValues.length > 0 ? [] : state.excludedValues,
  };
};

/**
 * 點排除鈕（排除的切換），回傳新的狀態；沒有變的那一邊回傳原本的陣列。
 *
 * - 多選：沒選 → 排除；包含 → 排除（同時從包含拿掉）；排除 → 沒選
 * - 單選：切換這個值的排除，並把包含的值清掉（包含與排除不並存）
 *
 * @param state 目前的狀態
 * @param value 被點的值
 * @param isMultiple 是不是多選
 */
export const toggleSelectMenuExclusion = <T extends string | number>(
  state: SelectMenuSelectionState<T>,
  value: T,
  isMultiple: boolean
): SelectMenuSelectionState<T> => {
  const excludedValues = state.excludedValues.includes(value)
    ? removeSelectMenuValue(state.excludedValues, value)
    : [...state.excludedValues, value];

  if (isMultiple) {
    return { values: removeSelectMenuValue(state.values, value), excludedValues };
  }

  return { values: state.values.length > 0 ? [] : state.values, excludedValues };
};
